import type {
  JobPool,
  PoolMember,
  CreateJobPoolOptions,
  CreateOutputOptions,
  DispatchableJob,
  DispatchedJob,
  EnqueueJobOptions,
  FindOrCreateJobPoolOptions,
  Job,
  JobOutput,
  JobsClientConfig,
  ListDispatchableJobsOptions,
  PaginatedResponse,
  PoolRef,
  PostProgressOptions,
  RegisterMemberOptions,
  SubmitOptions,
  UpdateJobOptions,
  UpdateMemberOptions,
} from './types';

/**
 * Client for the Whittle Jobs HTTP API (the generic claim layer, ADR 0005).
 *
 * The substrate verbs are:
 * - **Producer**: `enqueueJob(...)` — opaque inputs. The strategy contract that
 *   shapes those inputs lives in `@whittlelabs/loom`, not here.
 * - **Subscriber**: `subscribe(...)` — a generic claim loop; the consumer's
 *   `onJob` callback interprets the opaque payload. `listDispatchableJobs` /
 *   `acceptDispatch` / `submit` are exposed for tooling.
 */
export class JobsClient {
  private readonly baseUrl: string;
  private readonly apiKey?: string;
  private readonly accessToken?: string;
  private readonly getAccessToken?: (opts?: { forceRefresh?: boolean }) => Promise<string | null>;
  private readonly onResponse?: (response: Pick<Response, 'headers' | 'status'>) => void;

  constructor(config: JobsClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/$/, '');
    this.apiKey = config.apiKey;
    this.accessToken = config.accessToken;
    this.getAccessToken = config.getAccessToken;
    this.onResponse = config.onResponse;
  }

  /**
   * Headers for one request. The auth header is resolved per call so a
   * long-lived client picks up a refreshed token from `getAccessToken`
   * (the manager behind it re-mints before expiry) instead of pinning the
   * token it happened to hold at construction.
   */
  private async buildHeaders(forceRefresh = false): Promise<Record<string, string>> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (this.apiKey) {
      headers['X-API-Key'] = this.apiKey;
    } else if (this.getAccessToken) {
      const token = await this.getAccessToken({ forceRefresh });
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } else if (this.accessToken) {
      headers['Authorization'] = `Bearer ${this.accessToken}`;
    }
    return headers;
  }

  /**
   * Fetch with one auth retry. A 401 on a token-provider client usually means
   * the token was invalidated server-side (Keep restart, key rotation,
   * revoke-then-repair, clock skew) while the local expiry timer still thinks
   * it's valid. Force a re-mint and retry once before surfacing the failure.
   * `onResponse` (min-version contract) fires on the response we actually act
   * on. `body` is a string, so it is safe to resend.
   */
  private async fetchWithAuthRetry(
    method: string,
    url: string,
    body?: string,
  ): Promise<Response> {
    let response = await fetch(url, { method, headers: await this.buildHeaders(), body });
    if (response.status === 401 && this.getAccessToken) {
      response = await fetch(url, { method, headers: await this.buildHeaders(true), body });
    }
    this.onResponse?.(response);
    return response;
  }

  // ── Job Pools ──────────────────────────────────────────────

  async createJobPool(options: CreateJobPoolOptions): Promise<JobPool> {
    return this.request<JobPool>('POST', '/api/job-pools', options);
  }

  /**
   * Idempotent upsert by `(principal_class, principal_id, name)`. When the
   * pool exists, returns the existing row without modifying membership
   * constraints. The Sift provisioning hook relies on this semantics.
   */
  async findOrCreateJobPool(
    options: FindOrCreateJobPoolOptions,
  ): Promise<JobPool> {
    return this.request<JobPool>(
      'POST',
      '/api/job-pools?idempotent=true',
      options,
    );
  }

  async listJobPools(
    filters?: { principalClass?: string; principalId?: string; name?: string },
  ): Promise<JobPool[]> {
    const params = new URLSearchParams();
    if (filters?.principalClass) params.set('principalClass', filters.principalClass);
    if (filters?.principalId) params.set('principalId', filters.principalId);
    if (filters?.name) params.set('name', filters.name);
    const qs = params.toString();
    const { pools } = await this.request<{ pools: JobPool[]; total: number }>(
      'GET',
      `/api/job-pools${qs ? `?${qs}` : ''}`,
    );
    return pools;
  }

  async getJobPool(poolId: string): Promise<JobPool> {
    return this.request<JobPool>('GET', `/api/job-pools/${poolId}`);
  }

  async resolveJobPoolByName(name: string): Promise<JobPool | undefined> {
    const pools = await this.listJobPools({ name });
    return pools.find((p) => p.name === name);
  }

  /** Internal: resolve a `PoolRef` to a pool id. */
  async resolvePoolRef(ref: PoolRef): Promise<string> {
    if ('id' in ref) return ref.id;
    const pool = await this.resolveJobPoolByName(ref.name);
    if (!pool) throw new Error(`Job pool not found by name: "${ref.name}"`);
    return pool.id;
  }

  // ── Members ──────────────────────────────────────────────────────
  // A subscriber joins a pool by registering as a member of it. These
  // map to the server's `/api/job-pools/:poolId/members` routes.

  async listMembers(poolId: string): Promise<PoolMember[]> {
    const { members } = await this.request<{ members: PoolMember[]; total: number }>(
      'GET',
      `/api/job-pools/${poolId}/members`,
    );
    return members;
  }

  async registerMember(
    poolId: string,
    options: RegisterMemberOptions,
  ): Promise<PoolMember> {
    return this.request<PoolMember>(
      'POST',
      `/api/job-pools/${poolId}/members`,
      options,
    );
  }

  async updateMember(
    poolId: string,
    memberId: string,
    options: UpdateMemberOptions,
  ): Promise<PoolMember> {
    return this.request<PoolMember>(
      'PUT',
      `/api/job-pools/${poolId}/members/${memberId}`,
      options,
    );
  }

  // ── Jobs (producer) ──────────────────────────────────────────────

  /**
   * Enqueue a job into a job pool. The `inputs` are opaque to Jobs; a Shuttle
   * reads the strategy header inside them to decide how to run it (ADR 0005).
   */
  async enqueueJob(options: EnqueueJobOptions): Promise<Job> {
    const jobPoolId = await this.resolvePoolRef(options.pool);
    const body = {
      jobPoolId,
      instructions: options.instructions,
      maxAttempts: options.maxAttempts,
      deadlineDuration: options.deadlineDuration,
      priority: options.priority,
      tags: options.tags,
      clientRef: options.clientRef,
      clientContext: options.clientContext,
      inputs: options.inputs,
    };
    return this.request<Job>('POST', '/api/jobs', body);
  }

  async getJob(jobId: string): Promise<Job> {
    return this.request<Job>('GET', `/api/jobs/${jobId}`);
  }

  async updateJob(jobId: string, options: UpdateJobOptions): Promise<Job> {
    return this.request<Job>('PATCH', `/api/jobs/${jobId}`, options);
  }

  async cancelJob(jobId: string): Promise<Job> {
    return this.request<Job>('PUT', `/api/jobs/${jobId}`);
  }

  // ── Outputs ──────────────────────────────────────────────────────

  async listOutputs(jobId: string): Promise<JobOutput[]> {
    // A job's outputs drive Loom's workflow advancement, so completeness
    // matters here: page through until every output is collected rather than
    // returning only the server's default first page.
    const all: JobOutput[] = [];
    const pageSize = 100;
    for (let offset = 0; ; offset += pageSize) {
      const { outputs, total } = await this.request<{ outputs: JobOutput[]; total: number }>(
        'GET',
        `/api/jobs/${jobId}/output?limit=${pageSize}&offset=${offset}`,
      );
      all.push(...outputs);
      if (outputs.length === 0 || all.length >= total) break;
    }
    return all;
  }

  async createOutput(jobId: string, options: CreateOutputOptions): Promise<JobOutput> {
    return this.request<JobOutput>('POST', `/api/jobs/${jobId}/output`, options);
  }

  /**
   * Post a batch of live progress frames for a running job. Best-effort
   * telemetry: the service publishes it over core NATS and never derives
   * authoritative state from it, so callers should swallow failures rather than
   * let a dropped frame affect the job.
   */
  async postProgress(jobId: string, options: PostProgressOptions): Promise<void> {
    await this.request<unknown>('POST', `/api/jobs/${jobId}/progress`, options);
  }

  // ── Attendance (subscriber dispatch lifecycle) ───────────────────

  /**
   * Accept a dispatched job. The subscribe loop calls this; direct
   * callers should be rare. Returns the dispatched-job context the
   * subscriber needs to execute and submit.
   */
  async acceptDispatch(jobId: string): Promise<DispatchedJob> {
    return this.request<DispatchedJob>('POST', `/api/jobs/${jobId}/attendance`);
  }

  async submit(jobId: string, options: SubmitOptions): Promise<unknown> {
    return this.request<unknown>('PUT', `/api/jobs/${jobId}/attendance`, options);
  }

  // ── Discovery (rare) ─────────────────────────────────────────────

  async listDispatchableJobs(
    options?: ListDispatchableJobsOptions,
  ): Promise<PaginatedResponse<DispatchableJob> & { directives: Record<string, unknown> | null }> {
    const params = new URLSearchParams();
    if (options?.pools?.length) params.set('pools', options.pools.join(','));
    if (options?.tags?.length) params.set('tags', options.tags.join(','));
    if (options?.maxAttempts !== undefined) params.set('maxAttempts', String(options.maxAttempts));
    if (options?.minPriority !== undefined) params.set('minPriority', String(options.minPriority));
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));
    if (options?.sort) params.set('sort', options.sort);
    const qs = params.toString();
    const { jobs, total, directives } = await this.request<{
      jobs: DispatchableJob[];
      total: number;
      /** Pending member directives merged across the caller's active memberships. */
      directives?: Record<string, unknown> | null;
    }>('GET', `/api/my/jobs${qs ? `?${qs}` : ''}`);
    return {
      data: jobs,
      pagination: { total, limit: options?.limit ?? 25, offset: options?.offset ?? 0 },
      directives: directives ?? null,
    };
  }

  // ── HTTP plumbing ────────────────────────────────────────────────

  private async request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const url = `${this.baseUrl}${path}`;
    const response = await this.fetchWithAuthRetry(
      method,
      url,
      body ? JSON.stringify(body) : undefined,
    );
    const json = (await response.json()) as {
      success: boolean;
      data: T;
      error?: { code: string; message: string };
    };
    if (!response.ok || !json.success) {
      const errorMessage = json.error?.message ?? `Request failed with status ${response.status}`;
      const error = new Error(errorMessage) as Error & { code?: string; statusCode?: number };
      error.code = json.error?.code;
      error.statusCode = response.status;
      throw error;
    }
    return json.data;
  }
}

